/**
 * Pure layout for the shower hardware — the ONE place that decides where a
 * showerhead's arm, flange and head (or a valve trim's plate and handles)
 * sit inside the object's box. `showerRenderers.js` builds Three.js meshes
 * from it and the Showerhead / ShowerControls schematics project the same
 * parts to 2D, so the two surfaces cannot drift.
 *
 * Frame: object-local metres, origin at the box centre, +y up, +z into the
 * shower, wall plane at z = -depth/2. Every part comes back as a centre plus
 * a size (and a tilt where it is not axis-aligned). The layouts FILL the box:
 * the flange touches the top, the head the bottom and the front, so the
 * pipeline's declared dimensions are what the viewer sees.
 *
 * No Three.js, no React — importable from both sides and from tests.
 */
import {
  SHOWERHEAD,
  SHOWER_CONTROLS,
  hangingHosePoints,
} from './proceduralSpecs';

// ---------------------------------------------------------------------------
// Variants. No canonical field exists on the object today; both surfaces
// accept `subtype` (the only one `ProceduralObject.jsx` forwards to renderer
// options) plus the `style` / `headType` / `controlType` spellings the
// schematics already read.
// ---------------------------------------------------------------------------

export const SHOWERHEAD_VARIANTS = ['standard', 'rain', 'handheld', 'dual'];

export function resolveShowerheadVariant(fields = {}) {
  const raw = fields.subtype || fields.style || fields.headType;
  return SHOWERHEAD_VARIANTS.includes(raw) ? raw : 'standard';
}

export const SHOWER_CONTROLS_VARIANTS = [
  'single-handle',
  'two-handle',
  'thermostatic',
];

export function resolveShowerControlsVariant(fields = {}) {
  const raw = fields.subtype || fields.style || fields.controlType;
  if (raw === 'single-lever') return 'single-handle';
  return SHOWER_CONTROLS_VARIANTS.includes(raw) ? raw : 'single-handle';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Largest x in [lo, hi] for which `fits(x)` holds, assuming `fits` is true
 * on a prefix of the range (bisection; deterministic).
 */
function largestSatisfying(fits, lo, hi, iters = 40) {
  if (fits(hi)) return hi;
  if (!fits(lo)) return lo;
  let a = lo;
  let b = hi;
  for (let i = 0; i < iters; i++) {
    const m = (a + b) / 2;
    if (fits(m)) a = m;
    else b = m;
  }
  return a;
}

/** Wall escutcheon behind the arm, shrunk for a box too small for a 2-3/4" one. */
function flangeFor({ width, height }, wallZ) {
  const S = SHOWERHEAD;
  const r = Math.min(S.flangeRadius, width * 0.3, height * 0.2);
  return { r, t: S.flangeThick, z: wallZ + S.flangeThick / 2 };
}

/**
 * Fixed-head radius: the smaller of the catalogue max, the box width and the
 * largest head whose tilted silhouette still hangs below the arm (derived at
 * the 45° max drop). Never smaller than the pipe it hangs from.
 */
function fixedHeadRadius({ width, height }, flange) {
  const S = SHOWERHEAD;
  const belowArm =
    (height - flange.r) * Math.SQRT1_2 - (S.headThick + S.faceThick) / 2;
  return Math.max(
    2 * S.armPipeRadius,
    Math.min(S.headDiaMax / 2, width / 2, belowArm)
  );
}

/**
 * Arm out of the wall at the top of the box, one bend, then a straight drop
 * to a head tilted to continue the arm's line (spray face down-forward).
 * The head sits in the box's front-bottom corner; the drop angle is 45°
 * unless the box is too tall for that — then the drop steepens until the
 * arm still has `armRunMin` of straight pipe out of the flange.
 */
function tiltedHead(dims, { topY, headR }) {
  const { height, depth } = dims;
  const S = SHOWERHEAD;
  const wallZ = -depth / 2;
  const flange = flangeFor(dims, wallZ);
  const armY = topY - flange.r;
  const armR = S.armPipeRadius;
  // The head "unit" is the body plus the spray plate proud of its face; its
  // half-thickness along the axis is `te`, and the body centre sits half a
  // plate behind the unit centre.
  const te = (S.headThick + S.faceThick) / 2;
  const zMin = wallZ + flange.t + S.armRunMin;

  const at = (phi) => {
    const s = Math.sin(phi);
    const c = Math.cos(phi);
    // Half-extents of the tilted unit in the y–z plane.
    const halfY = headR * s + te * c;
    const halfZ = headR * c + te * s;
    const ucy = -height / 2 + halfY;
    const ucz = depth / 2 - halfZ;
    // Back-face centre = where the arm's ball joint meets the head.
    const by = ucy + te * c;
    const bz = ucz - te * s;
    const ez = bz - (armY - by) * Math.tan(phi);
    const cy = ucy + (S.faceThick / 2) * c;
    const cz = ucz - (S.faceThick / 2) * s;
    return { cy, cz, by, bz, ez, halfY, halfZ };
  };
  const tilt = largestSatisfying((p) => at(p).ez >= zMin, 0, S.dropAngleMax);
  const g = at(tilt);

  return {
    wallZ,
    armR,
    flange: { ...flange, y: armY },
    arm: { y: armY, z0: wallZ + flange.t, z1: g.ez },
    elbow: { y: armY, z: g.ez, r: armR * 1.4 },
    drop: { from: { y: armY, z: g.ez }, to: { y: g.by, z: g.bz } },
    joint: { y: g.by, z: g.bz, r: armR * 1.6 },
    head: {
      cy: g.cy,
      cz: g.cz,
      r: headR,
      t: S.headThick,
      faceT: S.faceThick,
      tilt,
      halfY: g.halfY,
      halfZ: g.halfZ,
    },
  };
}

// ---------------------------------------------------------------------------
// Showerhead variants
// ---------------------------------------------------------------------------

export function showerheadStandardLayout(dims) {
  const flange = flangeFor(dims, -dims.depth / 2);
  return {
    kind: 'standard',
    ...tiltedHead(dims, {
      topY: dims.height / 2,
      headR: fixedHeadRadius(dims, flange),
    }),
  };
}

/**
 * Rain: a straight arm at the top of the box, a short vertical stub, and a
 * thin flat disc at the bottom-front spraying straight down.
 */
export function showerheadRainLayout(dims) {
  const { width, height, depth } = dims;
  const S = SHOWERHEAD;
  const wallZ = -depth / 2;
  const flange = flangeFor(dims, wallZ);
  const armY = height / 2 - flange.r;
  const r = Math.max(
    2 * S.armPipeRadius,
    Math.min(
      S.rainHeadDiaMax / 2,
      width / 2,
      (depth - flange.t - S.armRunMin) / 2
    )
  );
  const cz = depth / 2 - r;
  // Body centre; the spray plate hangs `faceThick` below it to the box bottom.
  const cy = -height / 2 + S.faceThick + S.rainHeadThick / 2;
  const headTop = cy + S.rainHeadThick / 2;
  return {
    kind: 'rain',
    wallZ,
    armR: S.armPipeRadius,
    flange: { ...flange, y: armY },
    arm: { y: armY, z0: wallZ + flange.t, z1: cz },
    elbow: { y: armY, z: cz, r: S.armPipeRadius * 1.4 },
    stub: { z: cz, yTop: armY, yBot: Math.min(armY, headTop) },
    head: { cy, cz, r, t: S.rainHeadThick, faceT: S.faceThick },
  };
}

/**
 * Handheld: a 1" slide bar on two wall brackets, a holder part-way up, and
 * the wand leaning forward out of it. The head is a disc at the wand's tip
 * lying ALONG the wand — the wand meets its rim — so the spray face points
 * at right angles to the wand: out of the shower and a little down, never up
 * the wand. In the standard head's terms that is a disc tilted
 * `π/2 − lean`, so both surfaces draw it with the standard head's code.
 * The wand leans at a holder's natural angle (`wandTilt`), less only when
 * the box is too shallow for it; a deeper box is left under-filled in depth
 * rather than leaning the head out to its front. The holder height is then
 * set so the head touches the top.
 *
 * `hoseOutlet` — where the inspection found the hose leaving the wall
 * (`{x, y}` from the box centre on the wall plane, written by the
 * existing-conditions apply step) — adds the supply elbow there and the hose
 * hanging from the wand's foot down to it. The hose is the one part allowed
 * OUT of the box: the outlet can be a metre from the holder, and a box grown
 * to hold the hose would make the head's own box the hose's whole hang.
 */
export function showerheadHandheldLayout(dims, { hoseOutlet } = {}) {
  const { width, height, depth } = dims;
  const S = SHOWERHEAD;
  const wallZ = -depth / 2;
  const bar = {
    r: S.barRadius,
    z: wallZ + S.barStandoff,
    yTop: height / 2,
    yBot: -height / 2,
    capR: S.barRadius, // domed end caps, inside yBot..yTop
  };
  const bracketYs = [height / 2 - S.bracketInset, -height / 2 + S.bracketInset];
  const wandLen = Math.min(S.wandLen, height * S.wandLenMaxFrac);
  const headR = Math.max(
    S.wandRadius,
    Math.min(S.wandHeadDiaMax / 2, width / 2, wandLen * 0.2)
  );
  const headT = S.wandHeadThick;
  const te = headT / 2 + S.faceThick; // body centre → front of the spray plate
  // grip → head centre; the head's far rim is the wand's tip
  const toHead = wandLen * (1 - S.wandGripFrac) - headR;
  const down = wandLen * S.wandGripFrac; // grip → wand bottom
  // The holder stands the grip out from the bar just far enough that the
  // wand's foot, leaning back as the head leans forward, clears the bar's
  // front — a grip resting against the bar put the foot through the bar.
  // With a hose, the foot also leaves room for the hose's first bend, which
  // carries on back along the wand before turning down.
  const hasHose = hoseOutletUsable(hoseOutlet);
  const footClear = (phi) =>
    hasHose
      ? Math.max(
          S.wandRadius,
          S.hoseRadius + S.hoseBendLeadHandle * Math.sin(phi)
        )
      : S.wandRadius;
  const gripZ = (phi) => bar.z + bar.r + footClear(phi) + down * Math.sin(phi);
  // Along the wand the head reaches `headR` past its centre; across it the
  // spray plate stands `te` proud, facing forward-down.
  const front = (phi) =>
    gripZ(phi) + (toHead + headR) * Math.sin(phi) + te * Math.cos(phi);
  // The holder's natural lean, or less if the box is too shallow for it.
  const tilt = largestSatisfying(
    (p) => front(p) <= depth / 2 + 1e-9,
    S.wandTiltMin,
    S.wandTilt
  );
  const gz = gripZ(tilt);
  const s = Math.sin(tilt);
  const c = Math.cos(tilt);
  // Top of the head: the far rim, plus the back of the body (which faces up-back).
  const headTop = (toHead + headR) * c + (headT / 2) * s;
  const grip = { y: height / 2 - headTop, z: gz };
  const head = {
    cy: grip.y + toHead * c,
    cz: grip.z + toHead * s,
    r: headR,
    t: headT,
    faceT: S.faceThick,
    tilt: Math.PI / 2 - tilt, // the spray face's tilt, as the standard head's
  };
  // The wand runs a little way into the head past its rim, so they join.
  const join = toHead - headR * S.wandHeadJoinFrac;
  const from = { y: grip.y - down * c, z: grip.z - down * s };
  const to = { y: grip.y + join * c, z: grip.z + join * s };
  const wand = {
    r: S.wandRadius,
    tilt,
    from,
    to,
    ...wandHandleParts(from, to, headT, headR * S.wandHeadJoinFrac),
  };
  return {
    kind: 'handheld',
    wallZ,
    bar,
    brackets: {
      r: S.bracketRadius,
      ys: bracketYs,
      z0: wallZ,
      z1: bar.z,
      flangeR: S.bracketFlangeRadius,
      flangeT: S.flangeThick,
    },
    holder: { y: grip.y, z0: bar.z, z1: grip.z, r: S.wandRadius * 0.7 },
    holderClamp: {
      y: grip.y,
      z: bar.z,
      r: bar.r + S.holderClampRadiusAdd,
      len: S.holderClampLen,
    },
    collar: {
      y: grip.y,
      z: grip.z,
      r: S.holderSleeveRadius,
      len: S.holderSleeveLen,
    },
    wand,
    head,
    ...hoseParts(wallZ, wand, hoseOutlet),
  };
}

/**
 * The handle's own shape along the wand from `from` (its foot) to `to`
 * (inside the head): a conical swivel nut at the foot, tapering from the
 * grip down to the hose, and over the last `wandNeckLen` a neck that
 * flattens the round grip into a paddle as wide as `wandNeckHalfWidth`
 * either side and no thicker than the head (`headT`), so the handle grows
 * into the head instead of a pipe meeting a puck. The grip proper runs
 * from `nut.to` to `neck.from`. Points along the wand only; the renderer
 * sweeps the neck's cross-sections, the schematics draw the silhouette.
 */
function wandHandleParts(from, to, headT, rimInset) {
  const S = SHOWERHEAD;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const len = Math.hypot(dy, dz);
  const along = (d) => ({
    y: from.y + (dy * d) / len,
    z: from.z + (dz * d) / len,
  });
  const nutLen = Math.min(S.wandNutLen, len * 0.15);
  // The neck is fully flattened where it enters the head's rim, `rimInset`
  // short of `to`, and keeps that section on into the head.
  const neckLen = Math.min(S.wandNeckLen, (len - rimInset) * 0.4);
  const neckStart = len - rimInset - neckLen;
  return {
    nut: { from, to: along(nutLen), r0: S.hoseRadius, r1: S.wandNutRadius },
    neck: {
      from: along(neckStart),
      to,
      fullFrac: neckLen / (len - neckStart), // of the neck's length, where the flattening is complete
      r: S.wandRadius,
      halfWidth: S.wandNeckHalfWidth,
      halfThick: headT / 2,
    },
  };
}

/** The supply elbow at `hoseOutlet` and the hose from the wand's foot to it. */
function hoseOutletUsable(hoseOutlet) {
  return Boolean(
    hoseOutlet && Number.isFinite(hoseOutlet.x) && Number.isFinite(hoseOutlet.y)
  );
}

function hoseParts(wallZ, wand, hoseOutlet) {
  if (!hoseOutletUsable(hoseOutlet)) return {};
  const S = SHOWERHEAD;
  const elbow = {
    x: hoseOutlet.x,
    y: hoseOutlet.y,
    z0: wallZ,
    z1: wallZ + S.elbowDepth,
    r: S.elbowRadius,
    flangeR: S.elbowFlangeRadius,
  };
  // Out of the handle's foot along the wand, off the elbow straight out of
  // the wall: the hose bends into its hang from both, never kinks at them.
  const points = hangingHosePoints(
    { x: 0, y: wand.from.y, z: wand.from.z },
    { x: elbow.x, y: elbow.y, z: elbow.z1 },
    S.hoseLen,
    24,
    {
      startDir: { x: 0, y: -Math.cos(wand.tilt), z: -Math.sin(wand.tilt) },
      endDir: { x: 0, y: 0, z: 1 },
      startLead: S.hoseBendLeadHandle,
      endLead: S.hoseBendLeadElbow,
      minBendRadius: S.hoseMinBendRadius,
    }
  );
  return { elbow, hose: { r: S.hoseRadius, points } };
}

/**
 * Dual: the standard fixed head on its arm, plus a handheld wand parked
 * upright in a wall bracket at the left edge of the box, its head at the tip
 * with the spray face turned out of the shower (tilt π/2).
 */
export function showerheadDualLayout(dims) {
  const { width, height, depth } = dims;
  const S = SHOWERHEAD;
  const wallZ = -depth / 2;
  const fixed = showerheadStandardLayout(dims);
  const wandLen = Math.min(S.wandLen, height);
  const headR = Math.max(
    S.wandRadius,
    Math.min(S.wandHeadDiaMax / 2, width / 4, wandLen * 0.2)
  );
  const x = -width / 2 + headR;
  // Bracket standoff, or further out when the wand is thicker than that.
  const z = wallZ + Math.max(S.barStandoff, S.wandRadius);
  const yTop = height / 2;
  const yBot = yTop - wandLen;
  const headCy = yTop - headR;
  const yJoin = headCy - headR * S.wandHeadJoinFrac; // where the wand meets the head
  return {
    kind: 'dual',
    wallZ,
    fixed,
    wand: {
      x,
      z,
      r: S.wandRadius,
      yTop,
      yBot,
      yJoin,
      ...wandHandleParts(
        { y: yBot, z },
        { y: yJoin, z },
        S.wandHeadThick,
        headR * S.wandHeadJoinFrac
      ),
      head: {
        r: headR,
        t: S.wandHeadThick,
        faceT: S.faceThick,
        cy: headCy,
        tilt: Math.PI / 2, // spray face straight out of the shower
      },
      holder: {
        y: yBot + wandLen * S.wandGripFrac,
        r: S.bracketRadius,
        z0: wallZ,
        z1: z,
      },
    },
  };
}

/**
 * `fields` are the object's own (`hoseOutlet`, for a hand shower the
 * inspection found); both surfaces pass the same ones.
 */
export function showerheadLayout(dims, variant, fields = {}) {
  switch (variant) {
    case 'rain':
      return showerheadRainLayout(dims);
    case 'handheld':
      return showerheadHandheldLayout(dims, { hoseOutlet: fields.hoseOutlet });
    case 'dual':
      return showerheadDualLayout(dims);
    default:
      return showerheadStandardLayout(dims);
  }
}

// ---------------------------------------------------------------------------
// Shower controls
// ---------------------------------------------------------------------------

/**
 * Valve trim in the box: plate(s) flush to the wall, handles reaching the
 * front of the box. A two-handle set and a thermostatic trim lay their two
 * handles along the box's LONG axis, so a portrait box (the pipeline's
 * 6" × 10") stacks them and a landscape one puts them side by side.
 *
 * Returns { wallZ, faceZ, plates: [...], handles: [...] } where a plate is
 * { shape: 'round', x, y, r, t } or { shape: 'rect', x, y, w, h, rx, t } and a
 * handle is { kind: 'lever' | 'cross' | 'knob', x, y, ... }.
 */
export function showerControlsLayout(dims, variant) {
  const { width, height, depth } = dims;
  const C = SHOWER_CONTROLS;
  const wallZ = -depth / 2;
  const plateT = Math.min(C.plateThick, depth * 0.3);
  const plateFront = wallZ + plateT;
  const faceZ = depth / 2;
  const horizontal = width >= height;
  const along = Math.max(width, height);
  const across = Math.min(width, height);
  const at = (u) => (horizontal ? { x: u, y: 0 } : { x: 0, y: u });
  const pair = [at(-along / 4), at(along / 4)];
  const base = { wallZ, faceZ, plateFront };

  if (variant === 'two-handle') {
    const r = Math.min(across / 2, (along - C.plateGap) / 4);
    const hubR = Math.min(C.hubRadius, r * 0.4);
    const crossZ = faceZ - C.crossRadius;
    return {
      ...base,
      plates: pair.map((p) => ({ shape: 'round', ...p, r, t: plateT })),
      handles: pair.map((p) => ({
        kind: 'cross',
        ...p,
        hub: {
          r: hubR,
          z0: plateFront,
          z1: Math.min(plateFront + C.hubLenMax, crossZ),
        },
        stem: { r: C.stemRadius, z0: plateFront, z1: crossZ },
        cross: {
          len: Math.min(C.crossLenMax, r * 1.4),
          r: C.crossRadius,
          z: crossZ,
        },
      })),
    };
  }

  if (variant === 'thermostatic') {
    const knobR = Math.min(
      across * C.knobRadiusFrac,
      along * C.knobRadiusAlongFrac
    );
    return {
      ...base,
      plates: [
        {
          shape: 'rect',
          x: 0,
          y: 0,
          w: width,
          h: height,
          rx: across * C.plateCornerFrac,
          t: plateT,
        },
      ],
      handles: pair.map((p) => ({
        kind: 'knob',
        ...p,
        knob: {
          r: knobR,
          z0: plateFront,
          z1: faceZ,
          faceT: SHOWERHEAD.faceThick,
        },
      })),
    };
  }

  // single-handle
  const round = along / across <= C.plateRoundMaxAspect;
  const plate = round
    ? { shape: 'round', x: 0, y: 0, r: across / 2, t: plateT }
    : {
        shape: 'rect',
        x: 0,
        y: 0,
        w: width,
        h: height,
        rx: across * C.plateCornerFrac,
        t: plateT,
      };
  const leverZ = faceZ - C.leverRadius;
  const hub = {
    r: Math.min(C.hubRadius, across * 0.2),
    z0: plateFront,
    z1: Math.min(plateFront + C.hubLenMax, leverZ - C.leverRadius),
  };
  const dx = Math.cos(C.leverAngle);
  const dy = Math.sin(C.leverAngle);
  // The bar lies in the plane z = leverZ and must stay inside the box.
  const reach = Math.min(
    (width / 2 - C.leverRadius) / dx,
    (height / 2 - C.leverRadius) / dy
  );
  return {
    ...base,
    plates: [plate],
    handles: [
      {
        kind: 'lever',
        x: 0,
        y: 0,
        hub,
        stem: { r: C.stemRadius, z0: hub.z1, z1: leverZ },
        lever: {
          len: Math.max(0, Math.min(C.leverLenMax, reach)),
          r: C.leverRadius,
          angle: C.leverAngle,
          z: leverZ,
        },
      },
    ],
  };
}
